import "globalSetUp";

import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { FullStory, init as initFullStory } from "@fullstory/browser";
import { IonApp } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { ErrorBoundary, setUser as SentryReactSetUser } from "@sentry/react";
import { LoadingInterstitial } from "swing-components";

import { AlertError, useAuth } from "~components";
import { AuthedRoutes, UnAuthedRoutes } from "~pages";

import { AppUrlListener } from "./AppUrlListener";
import { IntercomMessenger } from "./IntercomMessenger";
import { BUILD_PROFILE } from "./utils/build";

/********** Component **********/
/**
 * Swing Subs application entry point.
 */
export function App() {
  const { userInfo, status, isAuthenticating } = useAuth();

  // store authentication status in localStorage for `LiveUpdate` to check since it
  // does not have access to `useAuth` hook as this must be inside of `AuthProvider`
  useEffect(() => {
    window.localStorage.setItem("isAuthenticating", isAuthenticating ? "true" : "false");
  }, [isAuthenticating]);

  useEffect(() => {
    if (userInfo.id === undefined) return;

    // set Sentry when user exists
    // https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/identify-user/
    SentryReactSetUser({ id: userInfo.id, email: userInfo.email });

    if (BUILD_PROFILE !== "prod") return;

    // set Pendo when user exists and in prod
    // https://agent.pendo.io/
    pendo.initialize({ visitor: { id: userInfo.id } });

    // set FullStory when user exists and in prod
    // https://github.com/fullstorydev/fullstory-browser-sdk
    initFullStory({ orgId: "GMJQ", devMode: BUILD_PROFILE !== "prod" });
    FullStory("setIdentity", {
      uid: userInfo.dbid?.toString(),
      properties: {
        displayName: userInfo.name,
        sub: true,
        ...userInfo,
      },
    });

    // Track the platform as a custom event.
    // FullStory doesn't differentiate between mobile platforms well.
    FullStory("trackEvent", {
      name: "Session Platform",
      properties: {
        application_name: "Swing Subs",
        platform: Capacitor.getPlatform(),
      },
    });
  }, [userInfo]);

  /***** Render *****/
  return (
    <ErrorBoundary fallback={<AlertError margin={16} />}>
      <IonApp>
        <IonReactRouter>
          <AppUrlListener />
          {status === undefined && (
            <div
              css={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <LoadingInterstitial />
            </div>
          )}
          {/* TODO: @KoltonG - Can we merge all routing at this level by using
          rules like the OnboardingRoutes? */}
          {status === "AUTHENTICATED" && (
            <>
              <IntercomMessenger />
              <AuthedRoutes />
            </>
          )}
          {status === "UN_AUTHENTICATED" && <UnAuthedRoutes />}
        </IonReactRouter>
      </IonApp>
    </ErrorBoundary>
  );
}
